#!/usr/bin/env node

import esbuild, { BuildOptions, OutputFile, Plugin } from 'esbuild'
import JSZip, { file } from 'jszip'
import fsPromises from 'fs/promises'
import fs from 'fs'

import { postcssModules, sassPlugin } from 'esbuild-sass-plugin'
import path from 'path'
import { getCustomComponentLibraryConfig } from './util'
import { CustomComponentLibrariesLibraryIdRevisionsPostVersionBumpEnum } from './retool_client'
import { generateManifest } from './generate_manifest'
import { CustomComponentLibraryWrappedClient } from './customComponentLibraryWrappedClient'
import { ApiError } from './apiError'

export type EsBuildLocation = {
  file: string
  line: number // 1-based
  column: number // 0-based, in bytes
  length: number // in bytes
  lineText: string
  suggestion: string
}

export type EsBuildErrorDescription = {
  pluginName: string
  id: string
  text: string
  detail: null | object
  notes: { text: string; location: EsBuildLocation }[]
  location: EsBuildLocation
}

const defaultLoaderPlugin: Plugin = {
  name: 'default-loader',
  setup(build) {
    build.onLoad(
      {
        filter: /.*/,
      },
      async (args) => {
        // There are already loaders for these files.
        const regex =
          /\.js$|\.cjs$|\.mjs$|\.ts$|\.tsx$|\.mts$|\.cts$|\.jsx$|\.tsx$|\.json$|\.css$|\.txt$|\.module\.scss$/

        if (regex.test(args.path) || args.namespace !== 'file') {
          return undefined
        } else {
          const source = await fs.promises.readFile(args.path, 'utf8')
          return {
            contents: source,
            loader: 'file',
          }
        }
      },
    )
  },
}
const reactGlobalPlugin: Plugin = {
  name: 'react-global',
  setup(build) {
    build.onResolve({ filter: /^react$/ }, (args) => {
      return { path: args.path, namespace: 'react-global' }
    })

    build.onLoad({ filter: /.*/, namespace: 'react-global' }, () => {
      return {
        contents: `
          module.exports = window.ccc_support_React;
        `,
        loader: 'jsx',
      }
    })

    // Same for react-dom
    build.onResolve({ filter: /^react-dom$/ }, (args) => {
      return { path: args.path, namespace: 'react-dom-global' }
    })

    build.onLoad({ filter: /.*/, namespace: 'react-dom-global' }, () => {
      return {
        contents: `
          module.exports = window.ccc_support_ReactDOM;
        `,
        loader: 'jsx',
      }
    })

    build.onResolve({ filter: /^react-dom\/server$/ }, (args) => {
      return { path: args.path, namespace: 'react-dom-server-global' }
    })

    build.onLoad({ filter: /.*/, namespace: 'react-dom-server-global' }, () => {
      return {
        contents: `
        export const createRoot = () => {}
        export default { createRoot }
        `,
        loader: 'jsx',
      }
    })

    // FOr react-dom/client
    build.onResolve({ filter: /^react-dom\/client$/ }, (args) => {
      return { path: args.path, namespace: 'react-dom-client-global' }
    })

    build.onLoad({ filter: /.*/, namespace: 'react-dom-client-global' }, () => {
      return {
        contents: `
        export const { createRoot, hydrateRoot } = window.ccc_support_ReactDOMClient;
        export default { createRoot, hydrateRoot }
        `,
      }
    })

    // Same for react/jsx-runtime
    build.onResolve({ filter: /^react\/jsx-runtime$/ }, (args) => {
      return { path: args.path, namespace: 'react-jsx-runtime-global' }
    })

    build.onLoad({ filter: /.*/, namespace: 'react-jsx-runtime-global' }, () => {
      return {
        contents: `
          export const { Fragment, jsxs, jsx, default } = window.ccc_support_ReactJSXRuntime;
        `,
        loader: 'jsx',
      }
    })

    // Same for @tryretool/custom-component-support
    build.onResolve({ filter: /^@tryretool\/custom-component-support$/ }, (args) => {
      return { path: args.path, namespace: 'tryretool-custom-component-collections-global' }
    })

    build.onLoad({ filter: /.*/, namespace: 'tryretool-custom-component-collections-global' }, () => {
      return {
        contents: `
          export const { Retool } = window.ccc_support_RetoolCustomComponenCollections;
        `,
        loader: 'jsx',
      }
    })
  },
}

const ARCHIVEABLE_FILE_EXTENSIONS = new Set(['.js', '.css', '.json'])

const constructZipFile = async (outputPath: string, buildResult: esbuild.BuildResult<esbuild.BuildOptions>, test: boolean): Promise<Blob> => {
  const zip = new JSZip()

  if (test) {
    if (buildResult.outputFiles === undefined) {
      throw Error('Build result was created with no outputFiles.')
    }
    for (const file of buildResult.outputFiles) {
      const fileExtension = path.extname(file.path)
      if (ARCHIVEABLE_FILE_EXTENSIONS.has(fileExtension)) {
        zip.file(file.path, file.contents)
      }
    }
  } else {
    const filesInDir = await fsPromises.readdir(outputPath)
    for (const file of filesInDir) {
      const fileExtension = path.extname(file)
      if (ARCHIVEABLE_FILE_EXTENSIONS.has(fileExtension)) {
        zip.file(file, fs.createReadStream(path.join(outputPath, file)))
      }
    }
  }

  return zip.generateAsync({ type: 'blob', streamFiles: true })
}

const buildResultReport: () => Plugin = () => {
  return {
    name: 'build-result-report',
    setup(build) {
      build.onEnd(async (result) => {
        if (result.errors.length > 0) {
          console.log('Build failed ❌')
        } else {
          console.log('Build succeeded 🔨')
        }
      })
    },
  }
}

const zipAndSendPlugin: (
  outputPath: string,
  libraryUuid: string,
  retoolClient: CustomComponentLibraryWrappedClient,
  devMode: boolean,
  test: boolean,
  verbose?: boolean
) => Plugin = (outputPath, libraryUuid, retoolClient, devMode, test, verbose = false) => {
  return {
    name: 'zip-and-send',
    setup(build) {
      build.onEnd(async (result) => {
        if (result.errors.length === 0) {
          const blob = await constructZipFile(outputPath, result, test)
          console.log('Sending updated code to Retool 📨...')
          const maxTries = 5
          let updateSuccess = false,
            tries = 0

          do {
            tries++
            if (tries > 1) {
              // for each retry, wait a bit longer
              await new Promise((resolve) => setTimeout(resolve, tries * 1000))
              console.log(`Retrying... (${tries} of ${maxTries})`)
            }

            try {
              const result = await retoolClient.customComponentLibrariesLibraryIdRevisionsPost({
                libraryId: libraryUuid,
                versionBump: devMode
                  ? CustomComponentLibrariesLibraryIdRevisionsPostVersionBumpEnum.Dev
                  : CustomComponentLibrariesLibraryIdRevisionsPostVersionBumpEnum.Major,
                files: blob,
              })

              console.log('Updated code received by Retool ✅.')
              console.log(`Successfully created a new version (${result.data.version}) of the library.`)
              updateSuccess = true
            } catch (e) {
              if (e instanceof ApiError) {
                if (e.statusCode === 409) {
                  console.info(
                    'The custom component library is being updated by someone else at this time. Will retry in a few seconds...',
                  )
                } else {
                  console.error('Error creating new version of custom component library:', e.errorMessage)
                  
                  if (verbose) {
                    console.error('\n--- Verbose Error Details ---')
                    console.error('Status Code:', e.statusCode)
                    if (e.response) {
                      console.error('Response URL:', e.response.url)
                      console.error('Response Status:', e.response.status)
                      console.error('Response Status Text:', e.response.statusText)
                      console.error('Response Headers:', Object.fromEntries(e.response.headers.entries()))
                      
                      // Try to get response body if available
                      try {
                        const responseClone = e.response.clone()
                        const responseText = await responseClone.text()
                        console.error('Response Body:', responseText)
                        
                        // Try to parse as JSON for better formatting
                        try {
                          const jsonBody = JSON.parse(responseText)
                          console.error('Response Body (JSON):', JSON.stringify(jsonBody, null, 2))
                        } catch {
                          // Already logged as text above
                        }
                      } catch (bodyError) {
                        console.error('Could not read response body:', bodyError)
                      }
                    }
                    if (e.stack) {
                      console.error('Stack Trace:', e.stack)
                    }
                    console.error('Full Error Object:', e)
                    console.error('--- End Verbose Details ---\n')
                  }
                }
              } else {
                if (verbose) {
                  console.error('\n--- Verbose Error Details ---')
                  console.error('Non-ApiError caught:', e)
                  if (e instanceof Error) {
                    console.error('Error Name:', e.name)
                    console.error('Error Message:', e.message)
                    if (e.stack) {
                      console.error('Stack Trace:', e.stack)
                    }
                  }
                  console.error('--- End Verbose Details ---\n')
                }
                throw e
              }
            }
          } while (devMode && !updateSuccess && tries < maxTries)
        }
      })
    },
  }
}
const generateManifestPlugin = {
  name: 'generate-manifest',
  setup(build) {
    build.onStart(() => {
      console.log(`Generating & updating manifest...`)
      try {
        generateManifest()
      } catch (error) {
        if (error.errors) {
          return {
            errors: error.errors,
          }
        } else {
          throw error
        }
      }
    })
    build.onLoad({ filter: /.*retool-custom-component-manifest\.json$/ }, async (args) => {
      const contents = await fs.promises.readFile(args.path, 'utf8')
      return {
        contents,
        loader: 'file',
      }
    })
  },
}

const excludeVendorFromSourceMapPlugin = {
  name: 'excludeVendorFromSourceMapPlugin',
  setup(build) {
    const emptySourceMapAsBase64 = Buffer.from(JSON.stringify({ version: 3, sources: [''], mappings: 'A' })).toString(
      'base64',
    )
    build.onLoad({ filter: /node_modules.+\.(js|ts|jsx|tsx)$/ }, (args) => {
      return {
        contents: `${fs.readFileSync(
          args.path,
          'utf8',
        )}\n//# sourceMappingURL=data:application/json;base64,${emptySourceMapAsBase64}`,
        loader: 'default',
      }
    })
  },
}

const constructBuildOptions = ({
  entryPoint,
  outputPath,
  zipAndSendPluginOptions,
  devMode,
  test,
}: {
  entryPoint: string,
  outputPath: string,
  zipAndSendPluginOptions?: {
    libraryUuid: string,
    retoolClient: CustomComponentLibraryWrappedClient,
    verbose?: boolean,
  },
  devMode: boolean,
  test: boolean
}): BuildOptions => {
  const plugins = [
    defaultLoaderPlugin,
    reactGlobalPlugin,
    excludeVendorFromSourceMapPlugin,
    sassPlugin({
      filter: /\.module\.scss$/,
      transform: postcssModules({}),
    }),
    buildResultReport(),
    zipAndSendPluginOptions !== undefined ? zipAndSendPlugin(outputPath, zipAndSendPluginOptions.libraryUuid, zipAndSendPluginOptions.retoolClient, devMode, test, zipAndSendPluginOptions.verbose) : undefined,
    generateManifestPlugin,
  ].filter((x) => x !== undefined)

  const release: BuildOptions = {
    absWorkingDir: process.cwd(),
    entryPoints: [
      { out: 'components', in: entryPoint },
      { out: 'retool-custom-component-manifest.json', in: 'retool-custom-component-manifest.json' },
    ],
    bundle: true,
    outdir: outputPath,
    external: [
      'react',
      'react-dom',
      'react/jsx-runtime',
      'react-dom/server',
      'react-dom/client',
      '@tryretool/custom-component-support',
    ],
    format: 'esm',
    sourcemap: devMode ? 'inline' : false,
    minify: !devMode,
    treeShaking: true,
    jsx: 'automatic',
    logLevel: 'info',
    assetNames: '[name]',
    write: !test,
    plugins,
  }

  return {
    ...release,
  }
}

export const buildComponentAndDeploy = async (
  libraryUuid: string,
  retoolClient: CustomComponentLibraryWrappedClient,
  test: boolean,
  verbose = false
) => {
  const { entryPoint, outputPath } = getCustomComponentLibraryConfig()

  const buildOptions: BuildOptions = constructBuildOptions({ entryPoint, outputPath, zipAndSendPluginOptions: { libraryUuid, retoolClient, verbose }, devMode: false, test })

  return esbuild.build(buildOptions)
}

export const buildAndDeployDevContiniously = async (
  libraryUuid: string,
  retoolClient: CustomComponentLibraryWrappedClient,
) => {
  const { entryPoint, outputPath } = getCustomComponentLibraryConfig()

  try {
    const buildOptions: BuildOptions = constructBuildOptions({ entryPoint, outputPath, zipAndSendPluginOptions: { libraryUuid, retoolClient }, devMode: true, test: false })

    const context = await esbuild.context(buildOptions)

    context.watch()
  } catch (e) {
    console.error('Build failed:', e)
  }
}

export const build = async (
  devMode: boolean,
) => {
  const { entryPoint, outputPath } = getCustomComponentLibraryConfig()

  const buildOptions: BuildOptions = constructBuildOptions({ entryPoint, outputPath, devMode, test: false })

  await esbuild.build(buildOptions)
}
